/**
 * Where the protocol's promotion stands, with who promoted it and when: never promoted, promoted, stopped,
 * or out of date because the protocol was compiled again since (its templates keep serving as promoted).
 * Promote opens the flow; Stop promoting returns every reply to a draft and every write to asking, in one
 * step, and is offered while the templates serve: promoted or out of date.
 */

import { useState } from 'react';
import { AlertTriangle, Rocket } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePromotionPlan, usePromotionPreview, useStopPromotion } from '@/hooks/use-promotion';
import { getApiErrorMessage } from '@/lib/utils';
import { ImpactList } from './impact-list';
import { PromoteDialog } from './promote-dialog';
import { promotionLine } from './promotion-line';

interface PromotionCardProps {
	sopId: string;
	canEdit: boolean;
	/** The scenario the card sits under, when it does: Promote opens scrolled to it. */
	focusTypeId?: string | null;
}

export const PromotionCard = ({ sopId, canEdit, focusTypeId = null }: PromotionCardProps) => {
	const { data: plan } = usePromotionPlan(sopId);
	const stop = useStopPromotion();
	const preview = usePromotionPreview();
	const [promoteOpen, setPromoteOpen] = useState(false);
	const [confirmStop, setConfirmStop] = useState(false);
	// The confirm lists what Stop changes on every blueprint, so it is fetched as the confirm opens.
	const openStopConfirm = () => {
		preview.mutate({ sopId, scenarios: null });
		setConfirmStop(true);
	};
	const record = plan?.promotion ?? null;
	const line = promotionLine(record);
	// A compile leaves an out-of-date promotion's templates serving, so it can still be stopped.
	const serving = record?.status === 'active' || record?.status === 'outdated';
	// What the system took back since promoting: a write a supervisor rejected, a scenario that failed a later test.
	const demoted =
		record?.status === 'active'
			? record.events.filter((event) => event.kind === 'write_demoted' || event.kind === 'type_demoted')
			: [];

	const stopNow = async () => {
		try {
			await stop.mutateAsync({ sopId });
			toast.success('Stopped. Every reply is a draft and every write asks first.');
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not stop promoting'));
		}
	};

	return (
		<div className='flex flex-col gap-1.5 rounded-md border border-gray-300 p-2' data-testid='promotion-card'>
			<div className='flex items-center justify-between gap-2'>
				<Badge variant={line.variant} className='text-[10px]'>
					{line.label}
				</Badge>
				{canEdit && (
					<div className='flex gap-1.5'>
						{serving && (
							<Button size='sm' variant='outline' disabled={stop.isPending} onClick={openStopConfirm}>
								Stop promoting
							</Button>
						)}
						<Button size='sm' onClick={() => setPromoteOpen(true)}>
							<Rocket className='size-3.5' aria-hidden />
							{record?.status === 'active' ? 'Update promotion…' : 'Promote…'}
						</Button>
					</div>
				)}
			</div>
			<p className='text-[11px] text-gray-800'>{line.detail}</p>
			{demoted.map((event) => (
				<p key={`${event.at}-${event.detail}`} className='flex items-start gap-1 text-[11px] text-amber-700'>
					<AlertTriangle className='mt-0.5 size-3 shrink-0' aria-hidden />
					{event.detail}
				</p>
			))}

			<PromoteDialog open={promoteOpen} onOpenChange={setPromoteOpen} sopId={sopId} focusTypeId={focusTypeId} />
			<AlertDialog open={confirmStop} onOpenChange={setConfirmStop}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Stop promoting?</AlertDialogTitle>
						<AlertDialogDescription>
							Every reply of this protocol goes back to a draft and every write goes back to asking a supervisor first.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className='flex flex-col gap-1' data-testid='stop-impact'>
						<p className='text-xs font-semibold'>Also changes on the promoted blueprints</p>
						<ImpactList preview={preview.data} loading={preview.isPending} error={preview.isError} />
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={stopNow}>Stop promoting</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};
